/**
 * Deterministic rules for the Cloud Agents internal-app flow (v2): what kind
 * of upload can become a Next.js app on Cloudflare Workers. The browser runs
 * them before uploading, the backend again before staging. Anything they
 * cannot decide is left to the validator model.
 *
 * The user-facing requirements, with one section per code, live in
 * `integromat/internal-experiments` at `docs/app-requirements.md`.
 */

export const UPLOAD_FINDING_CODES = [
  'backstage-plugin',
  'monorepo',
  'standalone-server',
  'mobile-desktop-extension',
  'library-or-cli',
  'unsupported-framework',
  'non-js',
  'container-only',
  'native-modules',
  'no-app-found',
  'too-large-or-binary',
] as const;

export type UploadFindingCode = (typeof UPLOAD_FINDING_CODES)[number];

export type UploadFindingSeverity = 'blocking' | 'warning';

export interface UploadFinding {
  code: UploadFindingCode;
  severity: UploadFindingSeverity;
  /** Paths in the upload that triggered the finding, at most a few. */
  files: string[];
  problem: string;
  howToFix: string;
  /** Section of the requirements doc that explains the rule. */
  docsAnchor: string;
}

export const UPLOAD_KINDS = [
  'nextjs',
  'vite-react-spa',
  'cra-react-spa',
  'static-html',
  'unknown',
] as const;

export type UploadKind = (typeof UPLOAD_KINDS)[number];

export interface UploadAssessment {
  verdict: 'accepted' | 'rejected';
  detectedKind: UploadKind;
  findings: UploadFinding[];
}

export interface UploadEntry {
  path: string;
  /** Uncompressed size; size rules are skipped when unknown. */
  sizeBytes?: number;
}

export interface AssessUploadInput {
  entries: UploadEntry[];
  /** Text of every entry for which {@link isUploadManifestPath} holds. */
  manifests: Record<string, string>;
}

/** TechDocs page of the requirements doc; append `#<docsAnchor>`. */
export const APP_REQUIREMENTS_DOC_PATH =
  '/docs/default/component/internal-experiments/app-requirements/';

/** Manifests larger than this are not read. */
export const MAX_UPLOAD_MANIFEST_BYTES = 512 * 1024;

/** Cloudflare Workers' limit for a single static asset. */
export const MAX_UPLOAD_FILE_BYTES = 25 * 1024 * 1024;

export const ALWAYS_IGNORED_UPLOAD_DIRS = [
  'node_modules',
  'dist',
  'out',
  'coverage',
];
export const ALWAYS_IGNORED_UPLOAD_FILES = [
  'package-lock.json',
  'yarn.lock',
  'pnpm-lock.yaml',
  'bun.lockb',
];

/**
 * The default upload filter: dotfiles and dot-directories (including
 * `.git`), dependency and build output directories, and lockfiles.
 */
export function isDefaultIgnoredUploadPath(path: string): boolean {
  if (path.startsWith('.') || path.includes('/.')) return true;
  const segments = path.split('/');
  if (segments.some(s => ALWAYS_IGNORED_UPLOAD_DIRS.includes(s))) return true;
  return ALWAYS_IGNORED_UPLOAD_FILES.includes(segments[segments.length - 1]);
}

export function isUploadManifestPath(path: string): boolean {
  const name = basename(path);
  return name === 'package.json' || name === 'manifest.json';
}

const SERVER_PACKAGES = [
  'express',
  'fastify',
  'koa',
  'hapi',
  '@hapi/hapi',
  'restify',
  '@nestjs/core',
  '@hono/node-server',
  'socket.io',
];
const SERVER_SCRIPT =
  /\b(?:node|nodemon|ts-node|tsx|bun)\s+(?:\S+\/)?server\.[cm]?[jt]s\b/;
const NATIVE_PACKAGES = [
  'sqlite3',
  'better-sqlite3',
  'sharp',
  'bcrypt',
  'canvas',
  'puppeteer',
  'playwright',
];
const NATIVE_APP_PACKAGES = [
  'react-native',
  'expo',
  'electron',
  '@capacitor/core',
];
const UNSUPPORTED_FRAMEWORKS = [
  'vue',
  'nuxt',
  'svelte',
  '@sveltejs/kit',
  '@angular/core',
  'solid-js',
  '@solidjs/start',
  'astro',
  'gatsby',
  '@remix-run/react',
  'ember-source',
  '@builder.io/qwik',
];
const SUPPORTED_FRAMEWORKS = [
  'next',
  'react',
  'react-dom',
  'vite',
  'react-scripts',
];
const WORKSPACE_FILES = [
  'pnpm-workspace.yaml',
  'lerna.json',
  'nx.json',
  'turbo.json',
  'rush.json',
];
const NON_JS_FILES = [
  'go.mod',
  'requirements.txt',
  'pyproject.toml',
  'Pipfile',
  'setup.py',
  'pom.xml',
  'build.gradle',
  'build.gradle.kts',
  'Cargo.toml',
  'Gemfile',
  'composer.json',
  'mix.exs',
];
const NON_JS_EXTENSIONS = ['.csproj', '.fsproj', '.sln'];
const CONTAINER_FILE =
  /^(?:Dockerfile(?:\..+)?|Containerfile|(?:docker-)?compose(?:\..+)?\.ya?ml)$/;
const APP_CONFIG_FILE = /^app-config(?:\..+)?\.ya?ml$/;
const DATABASE_EXTENSIONS = [
  '.sqlite',
  '.sqlite3',
  '.db',
  '.mdb',
  '.accdb',
  '.dump',
  '.bak',
];
const SPREADSHEET_EXTENSIONS = ['.xlsx', '.xls', '.ods'];
const MAX_FILES_PER_FINDING = 5;

interface PackageManifest {
  path: string;
  name?: string;
  deps: Set<string>;
  runtimeDeps: Set<string>;
  scripts: Record<string, string>;
  raw: Record<string, unknown>;
}

function basename(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

function extension(path: string): string {
  const name = basename(path);
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot).toLowerCase() : '';
}

function parseJsonObject(text: string): Record<string, unknown> | undefined {
  try {
    const value: unknown = JSON.parse(text);
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

function keysOf(value: unknown): string[] {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? Object.keys(value)
    : [];
}

function parsePackage(path: string, text: string): PackageManifest | undefined {
  const raw = parseJsonObject(text);
  if (!raw) return undefined;
  const runtime = [
    ...keysOf(raw.dependencies),
    ...keysOf(raw.optionalDependencies),
    ...keysOf(raw.peerDependencies),
  ];
  const scripts: Record<string, string> = {};
  if (raw.scripts && typeof raw.scripts === 'object') {
    for (const [key, value] of Object.entries(raw.scripts)) {
      if (typeof value === 'string') scripts[key] = value;
    }
  }
  return {
    path,
    name: typeof raw.name === 'string' ? raw.name : undefined,
    deps: new Set([...runtime, ...keysOf(raw.devDependencies)]),
    runtimeDeps: new Set(runtime),
    scripts,
    raw,
  };
}

function hasAny(deps: Set<string>, names: string[]): string[] {
  return names.filter(name => deps.has(name));
}

function limit(files: string[]): string[] {
  return [...new Set(files)].slice(0, MAX_FILES_PER_FINDING);
}

function list(names: string[]): string {
  return names.map(name => `\`${name}\``).join(', ');
}

function detectKind(packages: PackageManifest[], hasHtml: boolean): UploadKind {
  const deps = new Set(packages.flatMap(pkg => [...pkg.deps]));
  if (deps.has('next')) return 'nextjs';
  if (deps.has('react-scripts')) return 'cra-react-spa';
  if (deps.has('vite') && deps.has('react')) return 'vite-react-spa';
  if (hasHtml) return 'static-html';
  return 'unknown';
}

/**
 * Checks an upload against the supported scope. Any blocking finding rejects
 * it. When unsure the rules stay silent and the validator model decides.
 */
export function assessUpload(input: AssessUploadInput): UploadAssessment {
  const entries = input.entries.filter(
    entry => !isDefaultIgnoredUploadPath(entry.path),
  );
  const paths = entries.map(entry => entry.path);
  const packages = paths
    .filter(path => basename(path) === 'package.json')
    .map(path =>
      input.manifests[path] === undefined
        ? undefined
        : parsePackage(path, input.manifests[path]),
    )
    .filter((pkg): pkg is PackageManifest => pkg !== undefined);
  const hasPackageJson = paths.some(path => basename(path) === 'package.json');
  const htmlFiles = paths.filter(path => basename(path) === 'index.html');
  const hasWebFrontend = hasPackageJson || htmlFiles.length > 0;
  const root = packages.find(pkg => pkg.path === 'package.json');

  const findings: UploadFinding[] = [];
  const add = (
    code: UploadFindingCode,
    severity: UploadFindingSeverity,
    files: string[],
    problem: string,
    howToFix: string,
  ) =>
    findings.push({
      code,
      severity,
      files: limit(files),
      problem,
      howToFix,
      docsAnchor: code,
    });

  if (!hasWebFrontend) {
    add(
      'no-app-found',
      'blocking',
      [],
      'We could not find a web app in your upload: there is no `package.json` and no `index.html`.',
      'Upload the folder that contains your app’s `package.json`, or the `index.html` of a static site.',
    );
  }

  const backstagePackages = packages.filter(
    pkg =>
      'backstage' in pkg.raw ||
      [...pkg.deps].some(dep => dep.startsWith('@backstage/')),
  );
  const appConfigs = paths.filter(path => APP_CONFIG_FILE.test(basename(path)));
  if (backstagePackages.length > 0 || appConfigs.length > 0) {
    add(
      'backstage-plugin',
      'blocking',
      [...backstagePackages.map(pkg => pkg.path), ...appConfigs],
      'This looks like a Backstage plugin or a Backstage app. Those run inside Backstage, not as a standalone web app.',
      'Contribute Backstage plugins to `integromat/backstage` instead. If you meant to build a separate web app, upload only that app.',
    );
  }

  const workspaceFiles = paths.filter(path =>
    WORKSPACE_FILES.includes(basename(path)),
  );
  const appPackages = packages.filter(
    pkg =>
      ['dev', 'start', 'build'].some(script => script in pkg.scripts) ||
      hasAny(pkg.deps, [...SUPPORTED_FRAMEWORKS, ...UNSUPPORTED_FRAMEWORKS])
        .length > 0,
  );
  if (
    (root && 'workspaces' in root.raw) ||
    workspaceFiles.length > 0 ||
    appPackages.length > 1
  ) {
    add(
      'monorepo',
      'blocking',
      [
        ...(root && 'workspaces' in root.raw ? [root.path] : []),
        ...workspaceFiles,
        ...(appPackages.length > 1 ? appPackages.map(pkg => pkg.path) : []),
      ],
      'Your upload contains several projects (a monorepo or workspace). One upload becomes exactly one app.',
      'Upload only the folder of the one web app you want to publish.',
    );
  }

  const serverPackages = packages.flatMap(pkg =>
    hasAny(pkg.runtimeDeps, SERVER_PACKAGES).map(dep => ({ pkg, dep })),
  );
  const serverScripts = packages.filter(pkg =>
    Object.values(pkg.scripts).some(script => SERVER_SCRIPT.test(script)),
  );
  if (serverPackages.length > 0 || serverScripts.length > 0) {
    const names = [...new Set(serverPackages.map(({ dep }) => dep))];
    add(
      'standalone-server',
      'blocking',
      [
        ...serverPackages.map(({ pkg }) => pkg.path),
        ...serverScripts.map(pkg => pkg.path),
      ],
      names.length > 0
        ? `Your app runs its own server (${list(names)}). Apps here run on Cloudflare Workers, which can’t host a standalone server.`
        : 'Your app starts its own Node.js server. Apps here run on Cloudflare Workers, which can’t host a standalone server.',
      'Remove the server and keep only the browser app. If the app needs a few endpoints, move them into Next.js Route Handlers before uploading.',
    );
  }

  const nativeAppPackages = packages.filter(
    pkg =>
      hasAny(pkg.deps, NATIVE_APP_PACKAGES).length > 0 ||
      [...pkg.deps].some(dep => dep.startsWith('@tauri-apps/')) ||
      keysOf(pkg.raw.engines).includes('vscode'),
  );
  const extensionManifests = paths.filter(path => {
    if (basename(path) !== 'manifest.json') return false;
    const manifest = parseJsonObject(input.manifests[path] ?? '');
    return manifest !== undefined && 'manifest_version' in manifest;
  });
  if (nativeAppPackages.length > 0 || extensionManifests.length > 0) {
    add(
      'mobile-desktop-extension',
      'blocking',
      [...nativeAppPackages.map(pkg => pkg.path), ...extensionManifests],
      'This is a mobile app, a desktop app or a browser or editor extension. Only web apps that run in the browser can be published here.',
      'Upload a web version of the app instead.',
    );
  }

  if (
    root &&
    ('bin' in root.raw ||
      (['main', 'module', 'exports'].some(key => key in root.raw) &&
        hasAny(root.deps, [...SUPPORTED_FRAMEWORKS, ...UNSUPPORTED_FRAMEWORKS])
          .length === 0 &&
        htmlFiles.length === 0))
  ) {
    add(
      'library-or-cli',
      'blocking',
      [root.path],
      'This looks like a library or a command-line tool, not a web app with pages.',
      'Upload a web app that uses the library, or publish the library as a package instead.',
    );
  }

  const unsupported = [
    ...new Set(
      packages.flatMap(pkg => hasAny(pkg.deps, UNSUPPORTED_FRAMEWORKS)),
    ),
  ];
  if (unsupported.length > 0) {
    add(
      'unsupported-framework',
      'blocking',
      packages
        .filter(pkg => hasAny(pkg.deps, UNSUPPORTED_FRAMEWORKS).length > 0)
        .map(pkg => pkg.path),
      `Your app is built with ${list(unsupported)}. We support Next.js, React single-page apps (Vite or Create React App) and static HTML sites.`,
      'Rebuild the app with React or Next.js, or ask DevProd to support this framework.',
    );
  }

  const nonJs = paths.filter(
    path =>
      NON_JS_FILES.includes(basename(path)) ||
      NON_JS_EXTENSIONS.includes(extension(path)),
  );
  if (nonJs.length > 0) {
    add(
      'non-js',
      'blocking',
      nonJs,
      hasWebFrontend
        ? 'Your upload contains code in another language (for example a Python or Go backend). Only the JavaScript web app can run here.'
        : 'Your upload is written in a language other than JavaScript or TypeScript. Apps here are JavaScript web apps.',
      hasWebFrontend
        ? 'Remove the non-JavaScript part and upload only the browser app.'
        : 'Upload a JavaScript or TypeScript web app instead.',
    );
  }

  const containerFiles = paths.filter(path =>
    CONTAINER_FILE.test(basename(path)),
  );
  if (containerFiles.length > 0) {
    if (hasWebFrontend) {
      add(
        'container-only',
        'warning',
        containerFiles,
        'Your upload has container files. They will be ignored: apps here run on Cloudflare Workers, not in containers.',
        'Nothing to do if the app works without them. Remove them to keep the app tidy.',
      );
    } else {
      add(
        'container-only',
        'blocking',
        containerFiles,
        'Your app is defined only as a container. Apps here run on Cloudflare Workers, not in containers.',
        'Upload the web app’s source code instead of a container definition.',
      );
    }
  }

  const nativeModules = [
    ...new Set(
      packages.flatMap(pkg => hasAny(pkg.runtimeDeps, NATIVE_PACKAGES)),
    ),
  ];
  if (nativeModules.length > 0) {
    add(
      'native-modules',
      'blocking',
      packages
        .filter(pkg => hasAny(pkg.runtimeDeps, NATIVE_PACKAGES).length > 0)
        .map(pkg => pkg.path),
      `Your app depends on ${list(nativeModules)}, which need native code or a real browser and can’t run on Cloudflare Workers.`,
      'Remove these dependencies or replace them with browser-friendly alternatives. Test tools like Playwright are fine as dev dependencies.',
    );
  }

  const dataFiles = entries.filter(entry => {
    const ext = extension(entry.path);
    if (DATABASE_EXTENSIONS.includes(ext)) return true;
    if (SPREADSHEET_EXTENSIONS.includes(ext)) return true;
    return (
      entry.sizeBytes !== undefined && entry.sizeBytes > MAX_UPLOAD_FILE_BYTES
    );
  });
  if (dataFiles.length > 0) {
    add(
      'too-large-or-binary',
      'blocking',
      dataFiles.map(entry => entry.path),
      'Your upload contains databases, spreadsheets, data dumps or very large files. Uploaded files are committed to a repository that many people can read.',
      'Remove these files. Keep only the source code and the images or assets the app shows.',
    );
  }

  return {
    verdict: findings.some(finding => finding.severity === 'blocking')
      ? 'rejected'
      : 'accepted',
    detectedKind: detectKind(packages, htmlFiles.length > 0),
    findings,
  };
}
